import * as React from 'react';
import cn from 'classnames';
import { useIntl } from 'react-intl';
import { useAtom } from 'jotai';

import { ChatAI, Help, ListBullets } from 'src/common/icons';
import Logo from 'src/components/Logo';
import { KEEP_OVERLAY_ATTR, SHELL_HEADER_ID } from 'src/hooks/useShellOverlay';
import { chatOpenAtom } from 'src/shared/chat';
import { config } from 'src/utils/config';
import useSidebarCollapse from 'src/components/AppSidebar/useSidebarCollapse';
import PromptBar from './PromptBar';
import AiCoachMark from './AiCoachMark';
import messages from './messages';
import styles from './AppHeader.module.scss';

export const AppHeader: React.FC = () => {
  const { formatMessage } = useIntl();
  // Built-in feature: the toggle shows whenever chat is enabled for this
  // deployment, regardless of the account addon. The panel itself renders a
  // read-only "Not enabled" state when the addon/API is unavailable.
  const showChat = Boolean(config.chatEnabled);
  const [chatOpen, setChatOpen] = useAtom(chatOpenAtom);

  const { toggleMobile, isMobile } = useSidebarCollapse();

  const handleHelpClick = () => {
    window.dispatchEvent(new CustomEvent('openHelp', { detail: {} }));
  };

  return (
    <header className={styles.header} id={SHELL_HEADER_ID} data-test="admin-nav-bar">
      <div className={styles.brand}>
        {isMobile && (
          <button
            type="button"
            className={styles.mobileToggle}
            onClick={toggleMobile}
            aria-label={formatMessage(messages.toggleNavigation)}
            data-test="sidebar-mobile-toggle"
          >
            <ListBullets />
          </button>
        )}
        <Logo />
      </div>

      <div className={styles.promptWrap}>
        <PromptBar />
        <AiCoachMark />
      </div>

      {/* Profile / organization / logout live in the account menu at the
          sidebar bottom (AppSidebar/AccountMenu). */}
      <div className={styles.right}>
        {showChat && (
          <button
            type="button"
            className={cn(styles.iconButton, styles.chatToggle, {
              [styles.iconButtonActive]: chatOpen,
            })}
            onClick={() => setChatOpen(!chatOpen)}
            aria-label={formatMessage(
              chatOpen ? messages.closeAi : messages.openAi,
            )}
            aria-pressed={chatOpen}
            title={formatMessage(
              chatOpen ? messages.closeAi : messages.openAi,
            )}
            data-test="header-chat-toggle"
            /* The panel opens beside a content-area overlay, not behind it, so
               this must not dismiss an open asset. */
            {...{ [KEEP_OVERLAY_ATTR]: true }}
          >
            <ChatAI />
          </button>
        )}

        {config.showHelpIcon && (
          <button
            type="button"
            className={cn(styles.iconButton, styles.helpButton)}
            onClick={handleHelpClick}
            aria-label={formatMessage(messages.help)}
            title={formatMessage(messages.help)}
            data-test="help-button"
          >
            <Help />
          </button>
        )}
      </div>
    </header>
  );
};

export default AppHeader;
